import type {Resource} from "./resource";
import axios, { AxiosResponse } from "axios";
import {getFormdata, getGetParameterString} from "../axiosFormWrapper";
import type {IResourceManager} from "./IResourceManager";
import {AssociativeArray} from "../../js/meta_data";
import {afterBrowserRepaintDo} from "../globalUtils";

export interface ResourceLoaderConstructorArgumentsWithOptionalResourceName {
    url:string;
    resourceName?:string;
    data?;
    dataType?:string;
    failureAction?:string;
    method?:string;
    failHandler?:(response)=>void;
}

export interface ResourceLoaderConstructorArguments extends ResourceLoaderConstructorArgumentsWithOptionalResourceName{
    resourceName:string;
}


export class ResourceLoader {
    private readonly url:string;
    private readonly resourceName:string;
    private readonly method:string;
    private readonly data;

    private readonly startedStatus:string="loading";
    private readonly successStatus:string="loaded";
    private readonly failedStatus:string="failed";
    private readonly failureAction:string;
    private tryNumber: number;
    private readonly dataType:string;
    private static resourceManager: IResourceManager;


    constructor(args : ResourceLoaderConstructorArguments) {
        this.method = args.method || "GET";
        this.url = args.url;
        this.data = args.data || {};
        this.resourceName = args.resourceName;
        this.failureAction = args.failureAction || "retryOnce";
        this.tryNumber = 1;
        this.dataType = args.dataType || "json";
        if (typeof(args.failHandler) !== "undefined") {
            this.failHandler = args.failHandler;
        }
    }

    public static setResourceManager(resourceManager:IResourceManager) {
        ResourceLoader.resourceManager = resourceManager;
    }

    retry () {
        this.tryNumber++;
        this.load();
    }

    successHandler (response) {
        ResourceLoader.resourceManager.publishResource(this.resourceName, response);
        ResourceLoader.resourceManager.appendStatus(this.resourceName, this.successStatus);
    }

    failHandler =response=>this.resourceLoadingFailed(response);

    private static waitSoPromiseCatchDoesNotCatchErrorsInSuccessHandler = callback=>afterBrowserRepaintDo(callback);

    load () {
        ResourceLoader.resourceManager.appendStatus(this.resourceName, this.startedStatus);
        // const settings = AjaxSettingGetter.getSettings(this.url, this.data, this.method, this.dataType);
        const promise = this.method === "GET" ? ResourceLoader.loadViaGET(this.url, this.data, this.dataType) : ResourceLoader.loadViaPost(this.url, this.data, this.dataType);
        promise.then(response=>ResourceLoader.waitSoPromiseCatchDoesNotCatchErrorsInSuccessHandler(()=>this.successHandler(response.data)))
            .catch(response=>this.failHandler(response.data))
    }

    private static loadViaGET(url:string, data, responseType):Promise<AxiosResponse> {

        return axios({url:ResourceLoader.getUrlWithParameters(url, data), method:"GET", responseType})
    }

    private static getUrlWithParameters(url:string, data:Object) {
        if(Object.keys(data).length === 0) {
            return url;
        }
        const questionMarkpresent = url.indexOf("?")>-1;
        return url + (questionMarkpresent ? "&" : "?") + getGetParameterString(data);
    }

    private static loadViaPost(url, data, responseType):Promise<AxiosResponse> {
        return axios({url:url, method:"POST", responseType, data:getFormdata(data)})
    }

    private executeRetryOnce(resource:Resource, response) {
        if (this.tryNumber === 1) {
            console.log(ResourceLoader.getFailureNotice(resource.resourceName, response));
            this.retry();
        } else {
            console.log(ResourceLoader.getFinalFailstring(resource.resourceName, response));
            ResourceLoader.resourceManager.appendStatus(this.resourceName, this.failedStatus);
            if(resource.failureAction !== null) {
                resource.failureAction();
            }
        }
    }

    private executeRetry(resource:Resource, response) {
        if (this.tryNumber > 4) {
            console.log(ResourceLoader.getFinalFailstring(resource.resourceName, response));
            ResourceLoader.resourceManager.appendStatus(this.resourceName, this.failedStatus);
            if(resource.failureAction !== null) {
                resource.failureAction();
            }

        } else {
            console.log(ResourceLoader.getFailureNotice(resource.resourceName, response));
            (resource.loader as ResourceLoader).retry();
        }
    }

    private static getFailureNotice(resourcename, response) {
        return "failed to load resource " + resourcename + "; The response was " + JSON.stringify(response);
    }

    private static getFinalFailstring(resourcename, response) {
        return "Giving up :( :" + ResourceLoader.getFailureNotice(resourcename, response);
    }

    resourceLoadingFailed (response) {
        const resource = ResourceLoader.resourceManager.getResourceByName(this.resourceName);

        if (resource === null) {
            console.error("resource " + this.resourceName + " not found!");
            return;
        }

        switch (this.failureAction) {
            case "retryOnce":
                this.executeRetryOnce(resource, response);
                return;
            case "retry":
                this.executeRetry(resource, response);
                return;
            case "fail":
                console.log(ResourceLoader.getFinalFailstring(resource.resourceName, response));
                if(resource.failureAction !== null) {
                    resource.failureAction();
                }
                return;
            default:
                console.error("unknown failureAction " + this.failureAction);
        }
    }
}

